import type { GroupProps } from '@byecode/ui'
import { Divider, Flex, Group, IconFont, Input, Switch, Text, Tooltip } from '@byecode/ui'
import type { ContainerBreakPointConfigProtocol, PageAbstract, PageShareConfig, PartialExcludesIdAndName } from '@lighthouse/core'
import { DevicePreviewEnum, EVENT_VARIABLE_TYPE } from '@lighthouse/core'
import { deepMerge, getPageWithMergedBreakPoint, ListItem4ByecodeUi, NO_NAME_PAGE, useAtomAction } from '@lighthouse/shared'
import { deepOmitNil } from '@lighthouse/tools'
import fastEqual from 'fast-deep-equal'
import { clone, findIndex, pick } from 'rambda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useLatest } from 'react-use'
import { debounce } from 'throttle-debounce'

import { updateLocalPageAtom, updateRemotePageAtom } from '@/atoms/page/action'
import { BreakPointProvider } from '@/contexts/BreakPointContext'
import { useCurrentBreakPointId, useMainBreakPoint } from '@/hooks/useApplication'
import { PAGE_CONFIG_UPDATE_ACTION, pageUndoRedoController } from '@/utils/undoRedo/page/controller'

import { BlockConfigureSegmentedControl } from '../BlockSettings/Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../BlockSettings/Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../BlockSettings/constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../BlockSettings/constants'
import { CollectionEvent } from '../CollectionEvent'
import { EventList } from '../EventList'
import { AutoLayoutSetting, DesignSetting, getBlockAndPageDesignLimit, SizeSetting } from '../StyleSetting'
import type { PageParameterFormSlice } from './formSlice'
import NavbarVisibility from './NavbarVisibility'
import { PageParameterAndVariable } from './ParameterAndVariable'
import { PageShare } from './Share'
import { PageVisibility } from './Visibility'

interface PageSettingProps {
    pageData: PageAbstract
    mode?: GroupProps['mode']
}

const pageEventTypes = [EVENT_VARIABLE_TYPE.PAGE_VISIT]

const PageSetting: React.FC<PageSettingProps> = ({ pageData, mode }) => {
    const breakPointId = useCurrentBreakPointId()
    const mainBreakPoint = useMainBreakPoint()
    const latestPage = useLatest(pageData)
    const mergePageContent = getPageWithMergedBreakPoint(breakPointId, mainBreakPoint, pageData)
    const latestMergePage = useLatest(mergePageContent)
    // const allPages = useCurrentLanguagePageList()
    const { id, shareConfig, breakPoint, breakPoints, name, collectEvents, parameterAndVariable, cursor, isKeepAlive, eventList } =
        mergePageContent
    const [activeTab, setActiveTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const { run: updateLocalPage } = useAtomAction(updateLocalPageAtom)
    const { run: updateRemotePage } = useAtomAction(updateRemotePageAtom)

    const debounceUpdateRemotePage = useMemo(
        () =>
            debounce(
                500,
                async (origin: PageAbstract, current: PageAbstract) => {
                    pageUndoRedoController.add({ action: PAGE_CONFIG_UPDATE_ACTION, payload: { prev: origin, next: current } })
                    await updateRemotePage(current)
                }
                // { atBegin: true }
            ),
        [updateRemotePage]
    )

    const updatePage = useCallback(
        (current: PageAbstract) => {
            updateLocalPage(current)
            debounceUpdateRemotePage(pageData, current)
        },
        [debounceUpdateRemotePage, pageData, updateLocalPage]
    )

    const handleSyncOne = useCallback(
        (name: string | string[], previewId: string) => {
            const page = clone(latestPage.current)
            if (previewId === mainBreakPoint && page.breakPoint.id === previewId) {
                return
            }
            const path = Array.isArray(name) ? name : [name]
            const mergeBreakPoint = latestMergePage.current.breakPoint
            const target = deepMerge(page.breakPoint, mergeBreakPoint, path)
            const index = findIndex(item => item?.id === previewId, page.breakPoints)
            if (index >= 0) {
                page.breakPoints[index].breakKeys = page.breakPoints[index].breakKeys?.filter(key => key !== name)
            }
            page.breakPoint = target
            updatePage?.(page)
        },
        [latestMergePage, latestPage, mainBreakPoint, updatePage]
    )

    const handleSyncAll = useCallback(
        (previewId: string) => {
            if (previewId === DevicePreviewEnum.DESKTOP) {
                return
            }
            const page = clone(latestPage.current)
            page.breakPoint = latestMergePage.current.breakPoint
            const index = findIndex(item => item?.id === previewId, page.breakPoints)
            if (index >= 0) {
                page.breakPoints[index].breakKeys = []
            }
            updatePage?.(page)
        },
        [latestMergePage, latestPage, updatePage]
    )

    const styleProps = getBlockAndPageDesignLimit('page')

    const methods = useForm<PageParameterFormSlice>({
        values: { shareConfig, breakPoint, name, collectEvents, parameterAndVariable, cursor, isKeepAlive, eventList }
    })

    const { control, watch } = methods

    useEffect(() => {
        const subscribe = watch((values, info) => {
            if (
                fastEqual(
                    deepOmitNil(
                        pick(
                            ['breakPoint', 'collectEvents', 'name', 'parameterAndVariable', 'shareConfig', 'isKeepAlive', 'eventList'],
                            latestMergePage.current
                        )
                    ),
                    deepOmitNil(values)
                )
            ) {
                return
            }
            const { shareConfig, breakPoint, isKeepAlive, ...rest } = values
            const validShareConfig = shareConfig
                ? (() => {
                      const config = { ...shareConfig } as PageShareConfig & { link?: string }
                      delete config.link
                      return config
                  })()
                : shareConfig
            const validBreakPoint = clone(breakPoint) as ContainerBreakPointConfigProtocol
            const page = clone({
                ...pageData,
                ...rest,
                id,
                shareConfig: validShareConfig,
                breakPoint: validBreakPoint,
                breakPoints,
                isKeepAlive
            })
            if (breakPointId !== mainBreakPoint && info.name?.includes('breakPoint')) {
                page.breakPoint = pageData.breakPoint
                const index = findIndex(item => item?.id === breakPointId, page.breakPoints)
                if (index >= 0) {
                    page.breakPoints[index] = {
                        ...validBreakPoint,
                        id: page.breakPoints[index]?.id
                    }
                } else {
                    page.breakPoints.push({
                        ...validBreakPoint,
                        id: breakPointId
                    })
                }
            }
            updatePage(page)
        })

        return () => {
            subscribe.unsubscribe()
        }
    }, [updatePage, id, watch, breakPoints, breakPointId, pageData.breakPoint, pageData, latestMergePage, mainBreakPoint])

    return (
        <BreakPointProvider onSyncOne={handleSyncOne} onSyncAll={handleSyncAll}>
            <FormProvider {...methods}>
                <Flex grow>
                    <BlockConfigureSegmentedControl value={activeTab} onChange={setActiveTab} />
                </Flex>
                {activeTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                    <>
                        <NavbarVisibility pageId={id} />
                        <Divider color="var(--color-gray-200)" />
                        <SizeSetting {...styleProps.size} />
                        <Divider />
                        <AutoLayoutSetting {...styleProps.layout} />
                        <Divider />
                        <Group label="样式">
                            <DesignSetting {...styleProps.design} />
                        </Group>
                        <Divider />
                        <Controller control={control} name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                    </>
                )}

                {activeTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                    <>
                        <Group label="基础">
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>页面名称</Text>
                                <Input
                                    maxLength={30}
                                    {...methods.register('name', {
                                        onChange: v => {
                                            if (!v) {
                                                return NO_NAME_PAGE
                                            }
                                        }
                                    })}
                                    styles={{
                                        wrapper: { width: 180 }
                                    }}
                                />
                            </ListItem4ByecodeUi>
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Flex gap={4} alignItems="center">
                                    <Text>离开页面时暂存该页面</Text>
                                    <Tooltip
                                        width={300}
                                        title="开启后，从其他页面返回该页面时会保留页面的全部状态（如表单输入值、滚动位置等）。注意：暂存的页面会占用内存资源，过多使用可能会导致页面操作卡顿，请按需使用。"
                                    >
                                        <IconFont type="Question" size={16} color="var(--color-gray-400)" />
                                    </Tooltip>
                                </Flex>
                                <Controller
                                    control={control}
                                    name="isKeepAlive"
                                    render={({ field }) => (
                                        <Switch checked={field.value} onChange={e => field.onChange(e.currentTarget.checked)} />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                        </Group>

                        <Divider color="var(--color-gray-200)" />

                        <PageParameterAndVariable />

                        <Divider color="var(--color-gray-200)" />

                        <PageShare />

                        <Divider color="var(--color-gray-200)" />

                        <Controller
                            control={control}
                            name="collectEvents"
                            render={({ field }) => (
                                <CollectionEvent eventTypes={pageEventTypes} value={field.value} onChange={field.onChange} />
                            )}
                        />
                        <Divider color="var(--color-gray-200)" />

                        <PageVisibility pageId={id} mode={mode} addMemberWithinPortal />
                    </>
                )}
                {activeTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && (
                    <Controller
                        name="eventList"
                        control={control}
                        render={({ field }) => <EventList blockType="page" value={field.value ?? []} onChange={field.onChange} />}
                    />
                )}
            </FormProvider>
        </BreakPointProvider>
    )
}
export default PageSetting
